import { createAsyncThunk, createSlice, nanoid } from '@reduxjs/toolkit';
import NetworkManager from '../../app/managers/networkManager';
import { config } from '../../app/utils/config';
import { showSuccessToast } from '../../app/utils/helpers';

const initialState = {
  list: [],
  teamsCollabs: [],
  addEditClientResp: null,
  opportunities: [],
  addOpportunityResp: null,
  subcontractors: [],
  skills: [],
  subcontractorCollabs: [],
  endingContracts: [],
  inactiveClients: [],
  providerTypes: [
    { label: 'Agence', value: 'Agence' },
    { label: 'Indépendant', value: 'Indépendant' },
    { label: 'Sous traitant', value: 'Sous traitant' },
    { label: 'Portage', value: 'Portage' },
    { label: 'Intérimaire', value: 'Intérimaire' },
  ]
}

export const clientsSlice = createSlice({
  name: 'clients',
  initialState,
  reducers: {
    addClient: (state, action) => {
      state.push({
        value: nanoid(),
        label: action.payload.name
      })
    },
    setTeamsCollabs: (state, action) => {
      state.teamsCollabs = action.payload
    },
    setAddEditClientResp: (state, action) => {
      state.addEditClientResp = action.payload;
    },
    setAddOpportunityResp: (state, action) => {
      state.addOpportunityResp = action.payload;
    },
    setsubcontractors(state, action) {
      state.subcontractors = action.payload;
    },
    setSubcontractorCollabs(state, action) {
      state.subcontractorCollabs = action.payload;
    },
    setInactiveClients(state, action) {
      state.inactiveClients = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(getClients.fulfilled, (state, action) => {
      if (
        action.payload.responseData &&
        action.payload.responseData["clients"]
      ) {
        let clientsList = [];
        for (const key in action.payload.responseData["clients"]) {
          let client = action.payload.responseData["clients"][key];
          clientsList.push({
            value: client['id'],
            label: client['name'],
            id: client['id'],
            nom: client["name"],
            vfact: client["vfact"],
            managers: client["managers"],
            createdAt: new Date(client['created_at']).getTime(),
            updatedAt: new Date(client['updated_at']).getTime(),
            createdBy: client["createdBy"],
            updatedBy: client["updatedBy"]
          })
        }
        state.list = clientsList
      }
    })
    builder.addCase(getTeamCollabs.fulfilled, (state, action) => {
      if (
        action.payload.responseData &&
        action.payload.responseData.status
      ) {
        state.teamsCollabs = action.payload.responseData.collabs;
      }
    })
    builder.addCase(addEditClient.fulfilled, (state, action) => {
      if (action.payload.responseData && action.payload.responseData.status) {
        showSuccessToast(action.payload.responseData.message);
        if (action.payload.responseData.id_db) {
          state.addEditClientResp = action.payload.responseData.id_db + "#" + action.payload.responseData.name;
        }
      }
    })
    builder.addCase(getOpportunities.fulfilled, (state, action) => {
      if (action.payload.responseData && action.payload.responseData.status) {
        state.opportunities = action.payload.responseData.opportunities;
      }
    })
    builder.addCase(addOpportunity.fulfilled, (state, action) => {
      if (action.payload.responseData && action.payload.responseData.status) {
        showSuccessToast(action.payload.responseData.message);
        state.addOpportunitieResp = action.payload.responseData.status;
      }
    })
    builder.addCase(getSubcontractors.fulfilled, (state, action) => {
      if (action.payload.responseData && action.payload.responseData.subcontractors) {
        const convertToDate = (d) => {
          if (d) {
            const date = new Date(d);
            date.setHours(0, 0, 0, 0);
            return date;
          }
          return null;
        }
        state.subcontractors = action.payload.responseData.subcontractors.map(sc => ({
          ...sc,
          label: sc.name,
          value: sc.id,
          date_attestation_urssaf: convertToDate(sc.date_attestation_urssaf),
          date_fin_attestation_urssaf: convertToDate(sc.date_fin_attestation_urssaf),
          date_attestation_fiscale: convertToDate(sc.date_attestation_fiscale),
          date_fin_attestation_fiscale: convertToDate(sc.date_fin_attestation_fiscale),
          date_fin_validite_rc: convertToDate(sc.date_fin_validite_rc),
          date_attestation_salarie_etranger: convertToDate(sc.date_attestation_salarie_etranger),
          date_fin_attestation_salarie_etranger: convertToDate(sc.date_fin_attestation_salarie_etranger),
          created_at: convertToDate(sc.created_at),
          updated_at: convertToDate(sc.updated_at),
          active_contracts: sc.active_contracts
        }))
      }
    });
    builder.addCase(getSkillsSettings.fulfilled, (state, action) => {
      if (action.payload.responseData && action.payload.responseData.skills) {
        state.skills = action.payload.responseData.skills
      }
    });
    builder.addCase(getSubcontractorCollabs.fulfilled, (state, action) => {
      if (action.payload.responseData && action.payload.responseData.status) {
        state.subcontractorCollabs = action.payload.responseData.collabs
      }
    });
    builder.addCase(editOpportunity.fulfilled, (state, action) => {
      if (action.payload.responseData && action.payload.responseData.status) {
        showSuccessToast(action.payload.responseData.message)
      }
    });
    builder.addCase(getEndingContracts.fulfilled, (state, action) => {
      if (action.payload.responseData && action.payload.responseData.status) {
        state.endingContracts = action.payload.responseData.ending_contracts
      }
    });
    builder.addCase(getInactiveClients.fulfilled, (state, action) => {
      if (action.payload.responseData && action.payload.responseData.status) {
        state.inactiveClients = action.payload.responseData.clients
      }
    })
  }
})

export const getClients = createAsyncThunk(
  "planning/getClients",
  async () => {
    try {
      let response = await NetworkManager.getInstance().getRequest(
        config.apiPaths.planning.getClients
      );
      return response;
    } catch (error) {
      return error;
    }
  }
);

export const getTeamCollabs = createAsyncThunk(
  "planning/getTeamCollabs",
  async (body) => {
    try {
      let response = await NetworkManager.getInstance().postRequest(
        config.apiPaths.planning.getTeamCollabs,
        body
      );
      return response;
    } catch (error) {
      return error;
    }
  }
);

export const addEditClient = createAsyncThunk(
  "planning/addEditClient",
  async (body) => {
    try {
      let response = await NetworkManager.getInstance().postRequest(
        config.apiPaths.planning.addEditClient,
        body
      );
      return response;
    } catch (error) {
      return error;
    }
  }
);
export const getInactiveClients = createAsyncThunk(
  "planning/getInactiveClients",
  async (body) => {
    try {
      let response = await NetworkManager.getInstance().postRequest(
        config.apiPaths.planning.getInactiveClients,
        body
      );
      return response;
    } catch (error) {
      return error;
    }
  }
);
export const getOpportunities = createAsyncThunk(
  "teams/getOpportunities",
  async (body) => {
    try {
      let response = await NetworkManager.getInstance().postRequest(
        config.apiPaths.teams.getOpportunities,
        body
      );
      return response;
    } catch (error) {
      return error;
    }
  }
);

export const addOpportunity = createAsyncThunk(
  "teams/addOpportunity",
  async (body) => {
    try {
      let response = await NetworkManager.getInstance().postRequest(
        config.apiPaths.teams.addOpportunity,
        body
      );
      return response;
    } catch (error) {
      return error;
    }
  }
);

export const getSubcontractors = createAsyncThunk(
  "subcontractors/getSubcontractors",
  async () => {
    try {
      let response = await NetworkManager.getInstance().postRequest(
        config.apiPaths.subcontractors.getSubcontractors
      );
      return response;
    } catch (error) {
      return error;
    }
  }
);

export const getSubcontractorCollabs = createAsyncThunk(
  "subcontractors/getSubcontractorCollabs",
  async (body) => {
    try {
      let response = await NetworkManager.getInstance().postRequest(
        config.apiPaths.subcontractors.getSubcontractorCollabs,
        body
      );
      return response;
    } catch (error) {
      return error;
    }
  }
);

export const addSubcontractor = createAsyncThunk(
  "subcontractors/addSubcontractor",
  async (body) => {
    try {
      let response = await NetworkManager.getInstance().postRequest(
        config.apiPaths.subcontractors.addSubcontractor,
        body
      );
      return response;
    } catch (error) {
      return error;
    }
  }
);

export const editSubcontractor = createAsyncThunk(
  "subcontractors/editSubcontractor",
  async (body) => {
    try {
      let response = await NetworkManager.getInstance().postRequest(
        config.apiPaths.subcontractors.editSubcontractor,
        body
      );
      return response;
    } catch (error) {
      return error;
    }
  }
);
export const getSkillsSettings = createAsyncThunk(
  "subcontractors/getSkillsSettings",
  async (body) => {
    try {
      let response = await NetworkManager.getInstance().postRequest(
        config.apiPaths.subcontractors.getSkillsSettings,
        body
      );
      return response;
    } catch (error) {
      return error;
    }
  }
);

export const addEditSkill = createAsyncThunk(
  "subcontractors/addEditSkill",
  async (body) => {
    try {
      let response = await NetworkManager.getInstance().postRequest(
        config.apiPaths.subcontractors.addEditSkill,
        body
      );
      return response;
    } catch (error) {
      return error;
    }
  }
);
export const removeSkill = createAsyncThunk(
  "subcontractors/removeSkill",
  async (body) => {
    try {
      let response = await NetworkManager.getInstance().postRequest(
        config.apiPaths.subcontractors.removeSkill,
        body
      );
      return response;
    } catch (error) {
      return error;
    }
  }
);

export const editOpportunity = createAsyncThunk(
  "teams/editOpportunity",
  async (body) => {
    try {
      let response = await NetworkManager.getInstance().postRequest(
        config.apiPaths.teams.editOpportunity,
        body
      );
      return response;
    } catch (error) {
      return error;
    }
  }
);

export const getEndingContracts = createAsyncThunk(
  "teams/getEndingContracts",
  async (body) => {
    try {
      let response = await NetworkManager.getInstance().postRequest(
        config.apiPaths.teams.getEndingContracts,
        body
      );
      return response;
    } catch (error) {
      return error;
    }
  }
);



export const {
  addClient,
  setTeamsCollabs,
  setAddEditClientResp,
  seteditCollabMissionResp,
  setAddTeamManagerResp,
  setAddOpportunitieResp,
  setsubcontractors,
  setSubcontractorCollabs,
  setInactiveClients
} = clientsSlice.actions

export default clientsSlice.reducer